import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { canUseCompatibleModelAsDefault } from "@rakazo/core";
import { Button, Disclosure, Input } from "@rakazo/ui-web";
import { Check, Copy } from "lucide-react";
import {
  ModelPreflightFeedback,
  modelPreflightTestingLabel,
} from "../../components/ModelConnectionPreflightNotice";

import type { ModelSettingsState } from "./use-model-settings";

export function ConnectionControls({
  acceptsKey,
  accountId,
  apiKey,
  beginSelectedSubscriptionSignIn,
  busy,
  cancelOAuthAttempt,
  cloudflareRoutingReady,
  codeCopied,
  connectKey,
  copyOAuthCode,
  credential,
  gatewayId,
  isCloudflareGateway,
  isOpenAiCompatible,
  oauth,
  oauthPending,
  pasteCode,
  pending,
  popupBlocked,
  preflightFailure,
  preflightSuccess,
  preflightTarget,
  preflightTesting,
  selected,
  setAccountId,
  setGatewayId,
  setPasteCode,
  submitOAuthCode,
  subscriptionSignIn,
  testApiKeyConnection,
  testOAuthConnection,
  updateApiKey,
}: Pick<
  ModelSettingsState,
  | "acceptsKey"
  | "accountId"
  | "apiKey"
  | "beginSelectedSubscriptionSignIn"
  | "busy"
  | "cancelOAuthAttempt"
  | "cloudflareRoutingReady"
  | "codeCopied"
  | "connectKey"
  | "copyOAuthCode"
  | "credential"
  | "gatewayId"
  | "isCloudflareGateway"
  | "isOpenAiCompatible"
  | "oauth"
  | "oauthPending"
  | "pasteCode"
  | "pending"
  | "popupBlocked"
  | "preflightFailure"
  | "preflightSuccess"
  | "preflightTarget"
  | "preflightTesting"
  | "selected"
  | "setAccountId"
  | "setGatewayId"
  | "setPasteCode"
  | "submitOAuthCode"
  | "subscriptionSignIn"
  | "testApiKeyConnection"
  | "testOAuthConnection"
  | "updateApiKey"
>) {
  const { t } = useLingui();
  return !isOpenAiCompatible ? (
    <>
      {subscriptionSignIn ? (
        <div className="mt-5 first:mt-0">
          {oauth ? (
            <div className="rounded-xl border border-border px-4 py-3">
              {oauth.mode === "auth-url" ? (
                <>
                  <p className="text-[13px] leading-[1.5] text-muted-foreground">
                    {popupBlocked ? (
                      <Trans>
                        Open{" "}
                        <a
                          href={oauth.verificationUri}
                          target="_blank"
                          rel="noreferrer"
                          className="text-foreground underline"
                        >
                          {new URL(oauth.verificationUri).hostname}
                        </a>{" "}
                        to finish signing in.
                      </Trans>
                    ) : (
                      <Trans>
                        Finish signing in at{" "}
                        <a
                          href={oauth.verificationUri}
                          target="_blank"
                          rel="noreferrer"
                          className="text-foreground underline"
                        >
                          {new URL(oauth.verificationUri).hostname}
                        </a>
                        . The final page may not load; paste its URL or code here.
                      </Trans>
                    )}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Input
                      value={pasteCode}
                      onChange={(e) => setPasteCode(e.target.value)}
                      aria-label={t`Authorization code or callback URL`}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="http://localhost:53692/callback?code=…"
                      className="text-foreground md:text-[13px]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!pasteCode.trim()}
                      onClick={() => void submitOAuthCode()}
                    >
                      <Trans>Submit</Trans>
                    </Button>
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-muted-foreground">
                    <Plural
                      value={Math.ceil(oauth.expiresInSeconds / 60)}
                      one="Waiting for sign-in — the link expires in about # minute."
                      other="Waiting for sign-in — the link expires in about # minutes."
                    />
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[13px] leading-[1.5] text-muted-foreground">
                    {popupBlocked ? (
                      <Trans>
                        Open{" "}
                        <a
                          href={oauth.verificationUri}
                          target="_blank"
                          rel="noreferrer"
                          className="text-foreground underline"
                        >
                          {oauth.verificationUri.replace(/^https:\/\//, "")}
                        </a>{" "}
                        and enter this code:
                      </Trans>
                    ) : (
                      <Trans>
                        A sign-in tab opened at{" "}
                        <a
                          href={oauth.verificationUri}
                          target="_blank"
                          rel="noreferrer"
                          className="text-foreground underline"
                        >
                          {oauth.verificationUri.replace(/^https:\/\//, "")}
                        </a>
                        . Enter this code there — this window keeps waiting:
                      </Trans>
                    )}
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    <p className="font-mono text-[22px] tracking-[0.2em] text-foreground">
                      {oauth.userCode}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyOAuthCode(oauth.userCode)}
                    >
                      {codeCopied ? (
                        <Check size={14} strokeWidth={1.8} aria-hidden="true" />
                      ) : (
                        <Copy size={14} strokeWidth={1.8} aria-hidden="true" />
                      )}
                      {codeCopied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
                    </Button>
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-muted-foreground">
                    <Plural
                      value={Math.ceil(oauth.expiresInSeconds / 60)}
                      one="Waiting for sign-in — the code expires in about # minute."
                      other="Waiting for sign-in — the code expires in about # minutes."
                    />
                  </p>
                </>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 -ml-2 text-muted-foreground"
                onClick={() => cancelOAuthAttempt()}
              >
                <Trans>Cancel</Trans>
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => beginSelectedSubscriptionSignIn()}
            >
              {oauthPending ? (
                <Trans>Starting…</Trans>
              ) : credential?.authKind === "oauth" ? (
                <Trans>Sign in again</Trans>
              ) : (
                (selected?.oauthLabel ?? t`Sign in`)
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={busy || preflightTesting}
            onClick={() => void testOAuthConnection()}
          >
            {modelPreflightTestingLabel(
              preflightTesting && preflightTarget === "sign-in",
              "sign-in",
            )}
          </Button>
          {preflightTarget === "sign-in" ? (
            <ModelPreflightFeedback success={preflightSuccess} failure={preflightFailure} />
          ) : null}
        </div>
      ) : null}

      {acceptsKey ? (
        <div className="mt-5 first:mt-0">
          {isCloudflareGateway ? (
            <>
              <label
                className="block text-[13.5px] text-muted-foreground"
                htmlFor="cloudflare-account-id"
              >
                <Trans>Account ID</Trans>
                <Input
                  id="cloudflare-account-id"
                  value={accountId}
                  onChange={(event) => setAccountId(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-2 h-10 text-foreground"
                />
              </label>
              <label
                className="mt-4 block text-[13.5px] text-muted-foreground"
                htmlFor="cloudflare-gateway-id"
              >
                <Trans>Gateway ID</Trans>
                <Input
                  id="cloudflare-gateway-id"
                  value={gatewayId}
                  onChange={(event) => setGatewayId(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-2 h-10 text-foreground"
                />
              </label>
            </>
          ) : null}
          {acceptsKey ? (
            <label
              className={`block text-[13.5px] text-muted-foreground ${isCloudflareGateway ? "mt-4" : ""}`}
              htmlFor="model-api-key"
            >
              {credential ? (
                <Trans>Replace API key</Trans>
              ) : subscriptionSignIn ? (
                <Trans>Or connect an API key</Trans>
              ) : (
                <Trans>API key</Trans>
              )}
              <Input
                id="model-api-key"
                value={apiKey}
                onChange={(event) => updateApiKey(event.target.value)}
                placeholder={credential?.hasKey ? t`Paste a replacement key` : "sk-…"}
                type="password"
                autoComplete="new-password"
                className="mt-2 h-10 text-foreground"
              />
            </label>
          ) : null}
          {acceptsKey && selected?.catalogProbe ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 rounded-full"
                disabled={busy || preflightTesting || apiKey.trim().length < 8}
                onClick={() => void testApiKeyConnection()}
              >
                {modelPreflightTestingLabel(
                  preflightTesting && preflightTarget === "api-key",
                  "api-key",
                )}
              </Button>
              {preflightTarget === "api-key" ? (
                <ModelPreflightFeedback success={preflightSuccess} failure={preflightFailure} />
              ) : null}
            </>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            className="mt-3 rounded-full"
            size="sm"
            disabled={busy || !cloudflareRoutingReady || apiKey.trim().length < 8}
            onClick={() => void connectKey()}
          >
            {pending === "connect" ? (
              <Trans>Saving…</Trans>
            ) : credential ? (
              <Trans>Replace API key</Trans>
            ) : (
              <Trans>Connect API key</Trans>
            )}
          </Button>
        </div>
      ) : null}

      {selected?.auth === "oauth" && !subscriptionSignIn ? (
        <p className="mt-5 text-[13px] leading-[1.5] text-muted-foreground first:mt-0">
          <Trans>
            This subscription sign-in is not available in Rakazo yet. Use a deployment credential or
            choose another provider.
          </Trans>
        </p>
      ) : null}
    </>
  ) : null;
}

export function CompatibleKeyControls({
  apiKey,
  busy,
  connectKey,
  credential,
  isOpenAiCompatible,
  openAiCompatibleReady,
  pending,
  updateApiKey,
}: Pick<
  ModelSettingsState,
  | "apiKey"
  | "busy"
  | "connectKey"
  | "credential"
  | "isOpenAiCompatible"
  | "openAiCompatibleReady"
  | "pending"
  | "updateApiKey"
>) {
  const { t } = useLingui();
  return isOpenAiCompatible ? (
    <div className="mt-5">
      <Disclosure className="text-[13.5px] text-muted-foreground" summary={<Trans>API key</Trans>}>
        <Input
          aria-label={t`API key`}
          value={apiKey}
          onChange={(event) => updateApiKey(event.target.value)}
          placeholder={credential?.hasKey ? t`Paste a replacement key` : t`Optional`}
          type="password"
          autoComplete="new-password"
          className="mt-2 h-10 text-foreground"
        />
      </Disclosure>
      <Button
        type="button"
        variant="secondary"
        className="mt-3 rounded-full"
        size="sm"
        disabled={busy || !openAiCompatibleReady}
        onClick={() => void connectKey()}
      >
        {pending === "connect" ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
      </Button>
    </div>
  ) : null;
}

export function SaveModelButton({
  busy,
  cloudflareRoutingReady,
  connectKey,
  credential,
  isActive,
  isOpenAiCompatible,
  modelId,
  pending,
  setModelDefault,
  settingsDirty,
  thinkingDirty,
  usingServerCredentials,
}: Pick<
  ModelSettingsState,
  | "busy"
  | "cloudflareRoutingReady"
  | "connectKey"
  | "credential"
  | "isActive"
  | "isOpenAiCompatible"
  | "modelId"
  | "pending"
  | "setModelDefault"
  | "settingsDirty"
  | "thinkingDirty"
  | "usingServerCredentials"
>) {
  return credential && (!isActive || thinkingDirty || usingServerCredentials || settingsDirty) ? (
    <div className="mt-6">
      <Button
        type="button"
        variant="secondary"
        className="rounded-full"
        size="sm"
        disabled={
          busy ||
          !cloudflareRoutingReady ||
          (isOpenAiCompatible &&
            (!modelId.trim() ||
              !canUseCompatibleModelAsDefault(credential.enabledModelIds, modelId.trim())))
        }
        onClick={() =>
          void (settingsDirty ? connectKey({ settingsOnly: true }) : setModelDefault())
        }
      >
        {pending === "default" ? (
          <Trans>Switching…</Trans>
        ) : pending === "settings" ? (
          <Trans>Saving…</Trans>
        ) : isActive && !usingServerCredentials ? (
          <Trans>Save</Trans>
        ) : (
          <Trans>Use this model</Trans>
        )}
      </Button>
    </div>
  ) : null;
}
